import React, { createContext, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { AppToast, AppToastVariant } from "../components/blocks/app-toast";
import { wait } from "../utils/helpers";
import { ToastOptions } from "../types/toast";

const toToastVariant = (
  toastType: ToastOptions["toastType"]
): AppToastVariant => {
  if (toastType === "information" || toastType === "info") return "info";
  if (
    toastType === "success" ||
    toastType === "error" ||
    toastType === "warning"
  ) {
    return toastType;
  }
  return "default";
};

type NotificationContextType = {
  addNotification: (props: ToastOptions) => void;
  removeNotification?: (id: string | number | Uint32Array) => void;
};

export const NotificationContext = createContext<NotificationContextType>({
  addNotification: () => true
});

export const NotificationContextProvider = ({ children }: any) => {
  const lastNotificationRef = useRef<ToastOptions | null>(null);

  const addNotification = useCallback((newNotification: ToastOptions) => {
    if (
      lastNotificationRef.current &&
      lastNotificationRef.current.heading === newNotification.heading
    ) {
      return;
    }

    lastNotificationRef.current = newNotification;

    let defaultTime = 3000;

    const messageLength =
      typeof newNotification.heading === "string"
        ? newNotification.heading.length
        : String(newNotification.heading ?? "").length;

    if (messageLength > 100 && messageLength <= 150) {
      defaultTime = 4000;
    } else if (messageLength > 150) {
      defaultTime = 5000;
    }

    const autoClose =
      newNotification.autoClose === undefined
        ? defaultTime
        : newNotification.autoClose;

    const duration = autoClose === false ? Infinity : autoClose;

    // All app toasts render through the shared themed AppToast surface so the
    // whole app matches (quiet card + tinted icon badge). Variant drives the
    // icon + accent; the message is whatever the caller passed as `heading`.
    toast.custom(
      () => (
        <AppToast
          variant={toToastVariant(newNotification.toastType)}
          message={newNotification.heading}
          action={newNotification.action}
        />
      ),
      { duration }
    );

    // A persistent toast (autoClose: false) never auto-dismisses, so there's
    // no duration to clear the dedup ref after — leave it set until a
    // different heading comes through, rather than lifting the guard almost
    // immediately via wait(0).
    if (autoClose !== false) {
      wait(autoClose).then(() => {
        lastNotificationRef.current = null;
      });
    }
  }, []);

  const notificationContextValue = useMemo(
    () => ({
      addNotification
    }),
    [addNotification]
  );

  return (
    <NotificationContext.Provider value={notificationContextValue}>
      {children}
      <Toaster position="bottom-right" />
    </NotificationContext.Provider>
  );
};

export const notification = (props: ToastOptions) => {
  const toastNotification = new CustomEvent<ToastOptions>("toastNotification", {
    detail: props
  });
  window.dispatchEvent(toastNotification);
};
